import { ChevronRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { FILE_STATUS_PHRASES, documentDisplayName, type BrainDocumentItem } from "@gb/contracts";
import { Button } from "@/components/ui/button";
import { useSetDocumentStatus } from "@/features/project/documents-api";
import { DocumentBody } from "./brain-item-row";
import { RemoveFile } from "./document-status";

/**
 * One file on a shelf that asks about a hidden status, with its answers in
 * the row (2026-09-26, tracker HID-11, the default the owner can change).
 *
 * Until then the row was one button and Keep and Remove were only on the
 * file's own screen. In the German set one shelf held 70 files, so a person had
 * to open 70 pages. Now each row keeps the file's name as the way to its
 * screen, and puts the answers under it:
 *
 * - a file the check asked about: **Hide it** and **Keep it**, with the same
 *   weight, as on the file's screen: it is a question, not a recommendation;
 * - a hidden file: **Keep it**, the main action.
 *
 * Remove is on both, quieter, and on a file the check was not sure of it names
 * the doubt and asks a second time (HID-12, `RemoveFile`). There is no answer
 * for the whole shelf here: the review after an import answers a group.
 */
export function StatusShelfRow({
  item,
  projectId,
  onOpen,
  onDone,
}: {
  item: BrainDocumentItem;
  projectId: string;
  onOpen: () => void;
  /** What was done, in a sentence: the row leaves the shelf once it is re-read. */
  onDone: (sentence: string) => void;
}) {
  const answer = useSetDocumentStatus(projectId);
  const [removing, setRemoving] = useState(false);
  const name = item.title ?? documentDisplayName(item.filename);
  const question = item.status.question;
  const busy = answer.isPending || removing;
  const reason = question ? question.reason : item.status.reason;

  return (
    <li className="border-b border-border-inner last:border-b-0">
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full cursor-pointer items-center gap-3 px-4 pt-3 pb-1.5 text-left transition-colors hover:bg-subtle"
      >
        <DocumentBody item={item} />
        <ChevronRight size={18} className="shrink-0 text-muted" aria-hidden />
      </button>
      <div className="flex flex-col gap-1.5 px-4 pb-3 pl-[52px]">
        {question && (
          <p className="text-[0.82rem] font-semibold text-ink">Is this {FILE_STATUS_PHRASES[question.status]}?</p>
        )}
        {reason && <p className="line-clamp-2 text-[0.8rem] leading-[1.45] text-ink-slate">{reason}</p>}
        <div role="group" aria-label={`Answer for ${name}`} className="flex flex-wrap items-center gap-2">
          {question && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() =>
                answer.mutate(
                  { documentId: item.id, status: question.status },
                  { onSuccess: () => onDone(`Hidden: “${name}”.`) },
                )
              }
            >
              {answer.isPending && answer.variables?.status === question.status ? (
                <Loader2 size={14} className="animate-spin" />
              ) : null}
              Hide it
            </Button>
          )}
          <Button
            variant={question ? "secondary" : "primary"}
            size="sm"
            className={question ? undefined : "hover:bg-brand-deep"}
            disabled={busy}
            onClick={() =>
              answer.mutate(
                { documentId: item.id, status: "this-project" },
                { onSuccess: () => onDone(`Kept: “${name}”. It is used in answers.`) },
              )
            }
          >
            {answer.isPending && answer.variables?.status === "this-project" ? (
              <Loader2 size={14} className="animate-spin" />
            ) : null}
            Keep it
          </Button>
          <RemoveFile
            projectId={projectId}
            documentId={item.id}
            doubt={question ?? item.status.wasAsked}
            disabled={answer.isPending}
            onBusy={setRemoving}
            onStart={() => answer.reset()}
            onRemoved={() => onDone(`Removed: “${name}”.`)}
          />
        </div>
        {answer.error && (
          <p role="alert" className="text-[0.85rem] font-semibold text-danger">
            {answer.error.message}
          </p>
        )}
      </div>
    </li>
  );
}
